'use client'

import Link from 'next/link'
import {
  ArrowRight,
  BookOpen,
  Headphones,
  Package,
  GraduationCap,
  LogIn,
  Calendar,
  Award,
  Heart,
  Users,
  Star,
} from 'lucide-react'

// ============================================================
// HOME / HUB INSTITUCIONAL — patyshiatsu.com.br/
// ============================================================

const PRODUTOS = [
  {
    icon: BookOpen,
    titulo: 'Livro Físico',
    subtitulo: 'Método M.A.O.S. — edição impressa',
    preco: 'R$ 97,90',
    chamada: 'Frete grátis para todo Brasil',
    href: '/livro',
    cor: 'from-amber-500 to-amber-700',
    corBadge: 'bg-amber-100 text-amber-800',
    bullets: ['240 páginas', 'Brochura', 'Frete grátis BR'],
  },
  {
    icon: Headphones,
    titulo: 'Audiolivro Digital',
    subtitulo: 'Narrado pela própria Paty',
    preco: 'R$ 47,90',
    chamada: 'Acesso imediato após pagamento',
    href: '/livro-digital',
    cor: 'from-blue-500 to-blue-700',
    corBadge: 'bg-blue-100 text-blue-800',
    bullets: ['16 capítulos / 5h 4min', 'PDF do livro incluso', 'Acesso vitalício'],
  },
  {
    icon: Package,
    titulo: 'Combo Completo',
    subtitulo: 'Físico + Audiolivro',
    preco: 'R$ 127,90',
    chamada: 'Economize R$ 17,90 vs comprar separado',
    href: '/combo',
    cor: 'from-emerald-500 to-emerald-700',
    corBadge: 'bg-emerald-100 text-emerald-800',
    bullets: ['Livro físico + audiolivro', 'Bônus: ficha de prática', 'Combo mais vendido'],
    destaque: true,
  },
  {
    icon: GraduationCap,
    titulo: 'Curso Método M.A.O.S.',
    subtitulo: 'Formação completa — pré-venda',
    preco: 'R$ 1.997',
    precoSecundario: 'Pré-venda — aulas em gravação',
    chamada: '21 aulas + comunidade + certificado',
    href: '/curso',
    cor: 'from-violet-600 to-violet-800',
    corBadge: 'bg-violet-100 text-violet-800',
    bullets: ['21 aulas em 4 módulos', 'Acesso vitalício', 'Preço promocional travado'],
  },
]

const PILARES = [
  { letra: 'M', nome: 'Meridianos', desc: 'Pontos de equilíbrio energético da medicina chinesa aplicados na massagem.' },
  { letra: 'A', nome: 'Anatomia', desc: 'Conhecimento do corpo que sustenta cada movimento técnico do método.' },
  { letra: 'O', nome: 'Ondas', desc: 'Ritmo único que combina shiatsu suave, massagem clássica e drenagem linfática.' },
  { letra: 'S', nome: 'Serviço', desc: 'A entrega que faz o cliente dizer: "nunca fiz uma massagem assim na vida".' },
]

export default function PatyShiatsuHubPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      {/* ── HEADER ─────────────────────────────────────────── */}
      <header className="bg-white border-b border-amber-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <img
              src="/paty/logo.png?v=2"
              alt="Paty Shiatsu - Método M.A.O.S."
              className="h-10 sm:h-14 w-auto"
            />
          </Link>
          <a
            href="/login"
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950 text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition shadow-md min-h-[40px]"
          >
            <LogIn className="h-4 w-4" />
            <span>Entrar</span>
          </a>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-violet-900 to-indigo-900 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-violet-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 md:py-24">
          <div className="grid md:grid-cols-[1fr_280px] gap-6 md:gap-12 items-center">
            {/* Logo destaque - aparece primeiro no mobile, depois à direita no desktop */}
            <div className="flex justify-center md:order-2">
              <div className="w-36 h-36 sm:w-44 sm:h-44 md:w-72 md:h-72 rounded-full bg-white flex items-center justify-center shadow-2xl ring-4 ring-amber-300/30 p-4 md:p-6">
                <img
                  src="/paty/logo.png?v=2"
                  alt="Paty Shiatsu"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            <div className="md:order-1 text-center md:text-left">
              <p className="text-amber-300 text-[11px] sm:text-sm font-semibold tracking-wider uppercase mb-3">
                25 anos formando massoterapeutas profissionais
              </p>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 md:mb-5">
                Paty Shiatsu
                <br />
                <span className="text-amber-400">Método M.A.O.S.</span>
              </h1>

              <p className="text-base md:text-lg text-violet-100 mb-6 md:mb-7 leading-relaxed">
                Aqui você encontra o livro, o audiolivro, o curso completo e o atendimento.
                Material que ensina a técnica única que integra shiatsu suave, massagem clássica
                e drenagem linfática em um único método.
              </p>

              <a
                href="#produtos"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-stone-900 px-6 py-3.5 rounded-xl font-bold text-base transition min-h-[48px] shadow-lg shadow-amber-500/20"
              >
                Ver produtos
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUTOS ──────────────────────────────────────── */}
      <section id="produtos" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-stone-50 scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">
              Escolha por onde começar
            </h2>
            <p className="text-sm sm:text-base text-stone-600 max-w-2xl mx-auto leading-relaxed">
              Do livro ao curso completo — material pensado pra cada momento da sua jornada
              como massoterapeuta.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
            {PRODUTOS.map((p) => {
              const Icon = p.icon
              return (
                <Link
                  key={p.href}
                  href={p.href}
                  className={`group relative bg-white rounded-2xl border-2 ${p.destaque ? 'border-emerald-500 shadow-xl' : 'border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md'} overflow-hidden transition-all`}
                >
                  {p.destaque && (
                    <div className="absolute top-4 right-4 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
                      Mais vendido
                    </div>
                  )}

                  {/* Topo colorido */}
                  <div className={`h-2 bg-gradient-to-r ${p.cor}`} />

                  <div className="p-5 sm:p-6 md:p-7">
                    <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${p.cor} flex items-center justify-center mb-3 sm:mb-4 shadow-sm`}>
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-bold text-stone-900 mb-1">{p.titulo}</h3>
                    <p className="text-xs sm:text-sm text-stone-500 mb-3 sm:mb-4">{p.subtitulo}</p>

                    <div className="mb-3 sm:mb-4">
                      <div className="text-2xl sm:text-3xl font-bold text-stone-900">{p.preco}</div>
                      {p.precoSecundario && (
                        <div className="text-xs sm:text-sm text-stone-500 mt-0.5">{p.precoSecundario}</div>
                      )}
                      <div className="text-xs text-stone-600 mt-1.5 sm:mt-2">{p.chamada}</div>
                    </div>

                    <ul className="space-y-1.5 mb-4 sm:mb-5">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r ${p.cor} shrink-0`} />
                          {b}
                        </li>
                      ))}
                    </ul>

                    <div className={`inline-flex items-center gap-1.5 text-sm font-semibold bg-gradient-to-r ${p.cor} bg-clip-text text-transparent group-hover:gap-2 transition-all`}>
                      Ver detalhes
                      <ArrowRight className="h-4 w-4 text-stone-700 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── PILARES M.A.O.S. ───────────────────────────────── */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-purple-700 text-xs sm:text-sm font-semibold tracking-wider uppercase mb-2 sm:mb-3">
              O método
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">
              4 pilares que sustentam a técnica
            </h2>
            <p className="text-sm sm:text-base text-stone-600 max-w-2xl mx-auto leading-relaxed">
              M.A.O.S. é mais do que sigla. É a base do que ensina a fazer cliente dizer
              <span className="italic"> &ldquo;nunca fiz uma massagem assim na vida&rdquo;</span>.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
            {PILARES.map((pilar, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl p-5 sm:p-6 border border-stone-200"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-purple-700 to-purple-900 flex items-center justify-center text-amber-300 text-xl sm:text-2xl font-bold mb-3 sm:mb-4 shadow-md">
                  {pilar.letra}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-stone-900 mb-1.5 sm:mb-2">{pilar.nome}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{pilar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOBRE A PATY ──────────────────────────────────── */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-purple-50 to-amber-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-[280px_1fr] gap-6 md:gap-12 items-center">
            {/* Foto da Paty */}
            <div className="flex justify-center">
              <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden shadow-xl ring-4 ring-amber-300/40">
                <img
                  src="/paty/foto-paty.jpg?v=2"
                  alt="Patrícia Soares - Paty Shiatsu"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-purple-700 text-xs sm:text-sm font-semibold tracking-wider uppercase mb-2 sm:mb-3">
                Quem é
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-4 sm:mb-5">
                Patrícia Soares
              </h2>
              <div className="space-y-3 text-sm sm:text-base text-stone-700 leading-relaxed">
                <p>
                  Massoterapeuta há <strong>mais de 25 anos</strong>, especialista em Medicina
                  Chinesa e Técnicas Integrativas.
                </p>
                <p>
                  Desenvolveu uma técnica exclusiva que combina shiatsu suave, massagem clássica
                  e drenagem linfática em movimentos únicos. Clientes de spas internacionais
                  dizem que nunca fizeram uma massagem assim na vida.
                </p>
                <p>
                  Atende em São Paulo e ensina o Método M.A.O.S. para massoterapeutas que querem
                  parar de competir por preço e construir autoridade no mercado.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-5 sm:mt-6">
                <div className="bg-white rounded-xl p-2.5 sm:p-3 text-center border border-stone-200 shadow-sm">
                  <div className="text-xl sm:text-2xl font-bold text-purple-700">25+</div>
                  <div className="text-[9px] sm:text-[10px] text-stone-500 uppercase tracking-wide mt-0.5">Anos de prática</div>
                </div>
                <div className="bg-white rounded-xl p-2.5 sm:p-3 text-center border border-stone-200 shadow-sm">
                  <div className="text-xl sm:text-2xl font-bold text-purple-700">M.A.O.S.</div>
                  <div className="text-[9px] sm:text-[10px] text-stone-500 uppercase tracking-wide mt-0.5">Método autoral</div>
                </div>
                <div className="bg-white rounded-xl p-2.5 sm:p-3 text-center border border-stone-200 shadow-sm">
                  <div className="text-xl sm:text-2xl font-bold text-purple-700">SP</div>
                  <div className="text-[9px] sm:text-[10px] text-stone-500 uppercase tracking-wide mt-0.5">Atendimento</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ATENDIMENTO PESSOAL ───────────────────────────── */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <Calendar className="h-10 w-10 sm:h-12 sm:w-12 text-amber-400 mx-auto mb-3 sm:mb-4" />
          <h2 className="text-2xl sm:text-3xl md:text-3xl font-bold mb-3">
            Quer um atendimento pessoal?
          </h2>
          <p className="text-sm sm:text-base text-stone-300 mb-6 sm:mb-7 leading-relaxed max-w-xl mx-auto">
            Sessões presenciais em São Paulo. Atendimento individual para quem quer
            experimentar o método na própria pele antes de ensinar.
          </p>
          <a
            href="/l/funil"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-stone-900 px-6 py-3.5 rounded-xl font-bold text-base transition min-h-[48px] shadow-lg shadow-amber-500/20"
          >
            Agendar atendimento
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer className="bg-stone-950 text-stone-400 py-8 sm:py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8 text-sm">
            <div className="sm:col-span-2 md:col-span-1">
              <img
                src="/paty/logo.png?v=2"
                alt="Paty Shiatsu"
                className="h-14 w-auto mb-3 bg-white rounded-lg p-1.5"
              />
              <p className="text-stone-400 leading-relaxed">
                Massoterapia profissional pelo Método M.A.O.S. desde 2000.
              </p>
            </div>
            <div>
              <div className="font-bold text-white mb-3">Produtos</div>
              <ul className="space-y-2">
                <li><Link href="/livro" className="hover:text-amber-400 transition inline-block py-0.5">Livro físico</Link></li>
                <li><Link href="/livro-digital" className="hover:text-amber-400 transition inline-block py-0.5">Audiolivro digital</Link></li>
                <li><Link href="/combo" className="hover:text-amber-400 transition inline-block py-0.5">Combo completo</Link></li>
                <li><Link href="/curso" className="hover:text-amber-400 transition inline-block py-0.5">Curso Método M.A.O.S.</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-bold text-white mb-3">Mais</div>
              <ul className="space-y-2">
                <li><Link href="/l/funil" className="hover:text-amber-400 transition inline-block py-0.5">Agendar atendimento</Link></li>
                <li><a href="/login" className="hover:text-amber-400 transition inline-block py-0.5">Entrar (área de alunas)</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-stone-800 pt-5 sm:pt-6 text-center text-xs">
            <p>&copy; 2026 Paty Shiatsu — Método M.A.O.S. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
